import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { css, cx } from "@emotion/css"
import {
  disableClientTax,
  enableClientTax,
  fetchClientTaxes,
  setDefaultClientTax,
} from "api/taxes"
import { queryCache, useMutation, useQuery } from "react-query"

import { Container } from "components/Page"
import { EmptyState } from "components/EmptyState"
import { MarketTax } from "models/taxes"
import React from "react"
import { ReactComponent as StatsIcon } from "assets/campaign/icon-stats.svg"
import { Table } from "components/tables"
import { columns } from "./columns"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useSendFeedBack } from "components/FeedbackSnackbar"

type Props = {
  clientId: string
}

export default function AgencyClientTaxes(props: Props) {
  const { clientId } = props
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const actionAllowed = hasAuthority(["AUTH_VIEW_CLIENT_TAX"])
  const [modalInfo, setModalInfo] = React.useState<ModalInfo>()
  const feedback = useSendFeedBack()

  const isModalOpen = Boolean(modalInfo)

  const { data: taxes, isLoading } = useQuery(
    ["client-taxes", clientId],
    fetchClientTaxes,
  )

  const [enable] = useMutation(enableClientTax, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("client-taxes")
    },
    onError: (err) => {
      setModalInfo(undefined)
      feedback(t(err.message), "error")
    },
  })

  const [disable] = useMutation(disableClientTax, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries(["client-taxes"])
    },
    onError: (err) => {
      setModalInfo(undefined)
      feedback(t(err.message), "error")
    },
  })

  const [setDefault] = useMutation(setDefaultClientTax, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries(["client-taxes"])
    },
    onError: (err) => {
      setModalInfo(undefined)
      feedback(t(err.message), "error")
    },
  })

  const onEnable = React.useCallback(
    (taxId: string, clientId: string) => {
      setModalInfo({
        title: t("agency.client.taxes.modal.enable.title"),
        type: "primary",
        description: t("agency.client.taxes.modal.available.description"),
        submitLabel: t("modal.label.enable"),
        onSubmit: async () => await enable({ taxId, clientId }),
      })
    },
    [enable, t],
  )

  const onDisable = React.useCallback(
    (taxId: string, clientId: string) => {
      setModalInfo({
        title: t("agency.client.taxes.modal.disable.title"),
        type: "primary_destructive",
        description: t("agency.client.taxes.modal.disable.description"),
        submitLabel: t("modal.label.disable"),
        onSubmit: async () => await disable({ taxId, clientId }),
      })
    },
    [disable, t],
  )

  const onSetDefault = React.useCallback(
    (taxId: string, clientId: string) => {
      setModalInfo({
        title: t("agency.client.taxes.modal.set-default.title"),
        type: "primary",
        description: t("agency.client.taxes.modal.set-default.description"),
        submitLabel: t("modal.label.set-default"),
        onSubmit: async () => await setDefault({ taxId, clientId }),
      })
    },
    [setDefault, t],
  )

  const acTaxes = taxes?.filter((tax) => tax?.taxUsage === "AC")
  const mcTaxes = taxes?.filter((tax) => tax?.taxUsage === "MC")

  return (
    <Container>
      <h5 className={cx("BodyBody-1BlackBold", styles.title)}>
        {t("client.taxes.MC.title")}
      </h5>
      <Table<MarketTax>
        id="client-taxes-list-table"
        filters={[]}
        loading={isLoading}
        data={mcTaxes ?? []}
        total={mcTaxes?.length || 0}
        columns={columns(
          actionAllowed,
          t,
          clientId,
          onEnable,
          onDisable,
          onSetDefault,
        )}
        noResults={
          <EmptyState
            title={t("client.taxes.mc.noresults.title")}
            icon={<StatsIcon />}
            className={styles.emptyState}
          />
        }
        hasColumnsReordering={false}
        isPaginated={false}
        className={styles.table}
      />
      <h5 className={cx("BodyBody-1BlackBold", styles.title)}>
        {t("client.taxes.ac.title")}
      </h5>
      <Table<MarketTax>
        id="client-taxes-list-table"
        filters={[]}
        loading={isLoading}
        data={acTaxes ?? []}
        total={acTaxes?.length || 0}
        columns={columns(
          actionAllowed,
          t,
          clientId,
          onEnable,
          onDisable,
          onSetDefault,
        )}
        noResults={
          <EmptyState
            title={t("client.taxes.ac.noresults.title")}
            icon={<StatsIcon />}
            className={styles.emptyState}
          />
        }
        hasColumnsReordering={false}
        isPaginated={false}
        className={styles.table}
      />
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
    </Container>
  )
}

const styles = {
  title: css`
    padding: var(--spacing-xxl-4) 0 var(--spacing-xl) 0;
  `,
  table: css`
     > div 
       padding: 0;
     > table > tbody > tr {
       height: var(--spacing-xxl-3);
     }
     .MuiTable-stickyHeader {
       border-collapse: collapse;
     }
     .MuiTableHead-root {
       height: 40px;
     }
     .MuiTableCell-head {
       color: var(--black);
       font-weight: 700;
     }
     .MuiTableCell-root {
       border: 1px solid var(--light-grey);
     }
   `,
  emptyState: css`
    padding-top: 0;
    background-color: transparent;
    min-height: 188px;
  `,
}
